import type { VariableType } from './types';

const COLOR_RANGES: Record<string, [number, string][]> = {
  pressure_bar: [
    [2, '#0000FF'], [4, '#0044FF'], [5.5, '#0088FF'], [6.5, '#00CCFF'],
    [7.5, '#00FFCC'], [8.5, '#00FF66'], [9.5, '#88FF00'], [10.5, '#CCFF00'],
    [11.5, '#FFCC00'], [12.5, '#FF8800'], [13.5, '#FF4400'], [14.5, '#FF0000'],
  ],
  temperature_c: [
    [59, '#0000FF'], [64, '#0044FF'], [69, '#0088FF'], [74, '#00CCFF'],
    [79, '#00FFCC'], [84, '#00FF66'], [89, '#88FF00'], [94, '#CCFF00'],
    [99, '#FFCC00'], [104, '#FF8800'], [109, '#FF0000'],
  ],
  altitude: [
    [313, '#0000FF'], [325, '#0066FF'], [337, '#00CCFF'], [345, '#00FF66'],
    [352, '#88FF00'], [358, '#FFCC00'], [366, '#FF0000'],
  ],
  pressure_without_altitude_bar: [
    [2, '#0000FF'], [4, '#0044FF'], [6, '#0088FF'], [7, '#00CCFF'],
    [8.5, '#00FFCC'], [9.5, '#00FF66'], [10.5, '#88FF00'], [11.5, '#CCFF00'],
    [12.5, '#FFCC00'], [13.5, '#FF8800'], [14.5, '#FF0000'],
  ],
  speed_flow: [
    [0, '#0000FF'], [0.3, '#0066FF'], [0.6, '#00CCFF'], [0.9, '#00FF66'],
    [1.2, '#88FF00'], [1.5, '#FFCC00'], [2.0, '#FF8800'], [3.0, '#FF0000'],
  ],
  net_flow: [
    [0, '#0000FF'], [2, '#0066FF'], [5, '#00CCFF'], [10, '#00FF66'],
    [20, '#88FF00'], [40, '#FFCC00'], [80, '#FF8800'], [150, '#FF0000'],
  ],
  delta_pressure_bar: [
    [0, '#0000FF'], [0.5, '#0066FF'], [1, '#00CCFF'], [2, '#00FF66'],
    [3, '#88FF00'], [5, '#FFCC00'], [8, '#FF8800'], [12, '#FF0000'],
  ],
  gradient_loss: [
    [0, '#0000FF'], [50, '#0066FF'], [100, '#00CCFF'], [150, '#00FF66'],
    [200, '#88FF00'], [300, '#FFCC00'], [500, '#FF8800'], [1000, '#FF0000'],
  ],
  nominal_diameter: [
    [0.05, '#0000FF'], [0.1, '#0066FF'], [0.15, '#00CCFF'], [0.2, '#00FF66'],
    [0.25, '#88FF00'], [0.3, '#FFCC00'], [0.4, '#FF8800'], [0.5, '#FF0000'],
  ],
};

function getStops(variable: VariableType): [number, string][] {
  return COLOR_RANGES[variable] || COLOR_RANGES.pressure_bar;
}

interface LegendProps {
  nodeLabel: string;
  pipeLabel: string;
  nodeVariable: VariableType;
  pipeVariable: VariableType;
}

export default function Legend({ nodeLabel, pipeLabel, nodeVariable, pipeVariable }: LegendProps) {
  const nodeStops = getStops(nodeVariable);
  const pipeStops = getStops(pipeVariable);

  return (
    <div style={styles.container}>
      <div style={styles.columns}>
        {/* Nodes legend */}
        <div style={styles.column}>
          <div style={styles.title}>Nœuds</div>
          <div style={styles.subtitle}>{nodeLabel}</div>
          <div style={styles.stops}>
            {[...nodeStops].reverse().map(([value, color]) => (
              <div key={value} style={styles.row}>
                <span style={{ ...styles.swatch, backgroundColor: color }} />
                <span style={styles.value}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pipes legend */}
        <div style={styles.column}>
          <div style={styles.title}>Canalisations</div>
          <div style={styles.subtitle}>{pipeLabel}</div>
          <div style={styles.stops}>
            {[...pipeStops].reverse().map(([value, color]) => (
              <div key={value} style={styles.row}>
                <span style={{ ...styles.swatch, backgroundColor: color }} />
                <span style={styles.value}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'absolute',
    top: 12,
    right: 12,
    background: 'rgba(255,255,255,0.95)',
    borderRadius: 6,
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 10,
    fontSize: 11,
    maxHeight: 'calc(100% - 80px)',
    overflowY: 'auto',
  },
  columns: { display: 'flex', gap: 16 },
  column: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  title: { fontWeight: 700, fontSize: 12, marginBottom: 2 },
  subtitle: { color: '#666', marginBottom: 6 },
  stops: { display: 'flex', flexDirection: 'column', gap: 1 },
  row: { display: 'flex', alignItems: 'center', gap: 6 },
  swatch: { width: 16, height: 12, borderRadius: 2, display: 'inline-block' },
  value: { fontVariantNumeric: 'tabular-nums' },
};
